import { IconButton, Chip, TableRow, Typography, Box } from '@mui/material'
import * as React from 'react'
import { TimeSheet } from '../../../../types/Reports/ShiftEnd/TimeSheet'
import { Activity } from '../../../../types/Reports/ShiftEnd/Activity'
import { useShiftEndContext } from '../ShiftEndContext'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { StyledTableCell } from '../../../core/StyledTableCell'
import AnimatedNumberField from '../../../core/AnimatedNumberField'
import TimePickerCell from './TimePickerCell'
import RemoveRecordDialog from './RemoveRecordDialog'
import CommentPreview from './CommentPreview'
import CommentDialog from './CommentDialog'
import { useParams } from 'react-router-dom'
import { useDecodeToken } from '../../../../security/useDecodeToken'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import LowPriorityIcon from '@mui/icons-material/LowPriority'

dayjs.extend(duration)

const isEndTimeNextDay = (startTime: Date, endTime: Date | null): boolean =>
	!!endTime && dayjs(endTime).startOf('day').isAfter(dayjs(startTime).startOf('day'))

const getActivityTypeLabel = (activity: Activity) =>
	activity.activity === 'Constraint'
		? activity.type
		: activity.type.split(' > ').slice(0, -1).join(' > ')

type CommentTarget = {
	activityId: number
	comment: string
	activityLabel: string
	activityType: string
}

type ActivityRowProps = {
	timesheet: TimeSheet
	open: boolean
	handleAddTasks: (timeSheetId: number, timeRecordId?: number) => void
}

const ActivityRows = ({ timesheet, open, handleAddTasks }: ActivityRowProps) => {
	const { updateTimeSheet, removeActivity, updateActivityComment } = useShiftEndContext()
	const [commentTarget, setCommentTarget] = React.useState<CommentTarget | null>(null)

	const token = useDecodeToken()
	const params = useParams()
	const viewAsId = token?.role === 'Foreman' ? Number(token.nameid) : Number(params.id)

	const handleTimeChange = async (
		timesheet: TimeSheet,
		activity: Activity,
		startTimeDate: Date,
		endTimeDate: Date | null,
	) => {
		await updateTimeSheet(timesheet, activity, startTimeDate, endTimeDate)
	}

	const openCommentDialog = (activity: Activity) => {
		setCommentTarget({
			activityId: activity.id,
			comment: activity.comment ?? '',
			activityLabel: activity.activity,
			activityType: getActivityTypeLabel(activity),
		})
	}

	return (
		<>
			{open && (
				<>
					{(timesheet.activities ?? []).map((activity: Activity, index: number) => {
						return (
							<TableRow key={index} style={{ background: '#fff' }}>
								<StyledTableCell sx={{ verticalAlign: 'middle' }}>
									{!timesheet.confirmed && (
										<IconButton
											size='small'
											onClick={() => {
												handleAddTasks(timesheet.id, activity.id)
											}}
											aria-label='Add time record before this'
											sx={{
												backgroundColor: 'primary.main',
												color: 'white',
												'&:hover': { backgroundColor: 'primary.light', color: 'info.main' },
											}}
										>
											<LowPriorityIcon sx={{ transform: 'rotate(180deg) scaleX(-1)' }} />
										</IconButton>
									)}
								</StyledTableCell>
								<StyledTableCell align='center'>
									<Chip label={'Activity'} variant={'activity'} />
								</StyledTableCell>
								<StyledTableCell align='left'>{activity.activity}</StyledTableCell>
								<StyledTableCell align='left'>
									{/* <Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-start',
											gap: 0.5,
										}}
									> */}
										{activity.activity === 'Constraint'
											? activity.type
											: activity.type.split(' > ').map(
													(wi, index, arr) =>
														index !== arr.length - 1 && (
															<span key={index}>
																{index + 1}. {wi} <br />
															</span>
														),
											  )}
										{/* {activity.unpaidBreakHours != null && activity.unpaidBreakHours > 0 ? (
											<Typography
												variant='caption'
												sx={{
													color: 'warning.dark',
													fontWeight: 600,
													fontStyle: 'italic',
													whiteSpace: 'normal',
													wordBreak: 'break-word',
													overflowWrap: 'anywhere',
												}}
											>
												Includes {activity.unpaidBreakHours}h unpaid break.
											</Typography>
										) : null}
									</Box> */}
								</StyledTableCell>
								<StyledTableCell align='right'>
									<TimePickerCell
										canEdit={!timesheet.confirmed}
										timeValue={activity.startTime}
										onChange={async (newValue) => {
											await handleTimeChange(timesheet, activity, newValue, activity.endTime)
										}}
									/>
								</StyledTableCell>
								<StyledTableCell align='right'>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-end',
											gap: 0.5,
										}}
									>
										<TimePickerCell
											minTime={
												index === timesheet.activities.length - 1
													? new Date(activity.startTime)
													: undefined
											}
											canEdit={!timesheet.confirmed}
											timeValue={activity.endTime ? new Date(activity.endTime) : null}
											onChange={async (newValue) =>
												await handleTimeChange(timesheet, activity, activity.startTime, newValue)
											}
										/>
										{isEndTimeNextDay(activity.startTime, activity.endTime) && (
											<Typography
												variant='caption'
												sx={{ color: 'text.secondary', fontStyle: 'italic' }}
											>
												Ends next day (overnight)
											</Typography>
										)}
									</Box>
								</StyledTableCell>
								<StyledTableCell align='right'>
									<AnimatedNumberField
										value={activity.hours ? (Number.isNaN(activity.hours) ? 0 : activity.hours) : 0}
									/>
								</StyledTableCell>
								<StyledTableCell
									align='left'
									sx={{
										width: 180,
										maxWidth: 180,
										verticalAlign: 'middle',
										p: 1,
									}}
								>
									<CommentPreview
										comment={activity.comment ?? ''}
										canEdit={!timesheet.confirmed}
										onClick={() => openCommentDialog(activity)}
									/>
								</StyledTableCell>
								<StyledTableCell align='center'>
									{!timesheet.confirmed && (
										<RemoveRecordDialog
											onConfirm={async () => {
												await removeActivity(timesheet.id, activity.id, viewAsId)
											}}
										/>
									)}
								</StyledTableCell>
							</TableRow>
						)
					})}
					{!timesheet.confirmed && (
						<TableRow
							onClick={() => handleAddTasks(timesheet.id)}
							sx={{
								cursor: 'pointer',
								backgroundColor: 'action.hover',
								'&:hover': {
									backgroundColor: 'action.selected',
								},
								transition: 'background-color 0.2s ease',
							}}
						>
							<StyledTableCell />
							<StyledTableCell align='center' />
							<StyledTableCell align='left' colSpan={7}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1.5,
										py: 1,
									}}
								>
									<PlaylistAddIcon color='primary' sx={{ fontSize: 24 }} />
									<Typography
										variant='body1'
										sx={{
											color: 'primary.main',
											fontWeight: 500,
											userSelect: 'none',
										}}
									>
										Add Time Record
									</Typography>
								</Box>
							</StyledTableCell>
						</TableRow>
					)}
					<CommentDialog
						open={commentTarget !== null}
						onClose={() => setCommentTarget(null)}
						onSave={async (comment) => {
							if (!commentTarget) return
							await updateActivityComment(timesheet.id, commentTarget.activityId, comment)
						}}
						initialComment={commentTarget?.comment ?? ''}
						activityLabel={commentTarget?.activityLabel ?? ''}
						activityType={commentTarget?.activityType ?? ''}
						canEdit={!timesheet.confirmed}
					/>
				</>
			)}
		</>
	)
}

export default ActivityRows
